import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import {
  getAppType,
  getNativeAppVersion,
  getNativeBuildNumber,
  getNativeChannel,
  getTerminalId,
  getWebVersion,
} from '@/shared/bridge';
import { DEPLOY_MODE } from '@/shared/config';
import { UI_TEXT } from '@/shared/languages';
import { present } from '@/shared/overlay';
import { ROUTES } from '@/shared/routing';
import { useDebugStore } from '@/shared/stores';
import { Button, ELEVATED_SURFACE, FullScreenDialog } from '@/shared/ui';
import { cn } from '@herit-fe/heritage-ui';

/**
 * 히든 디버그 진단 — **이 파일 하나**가 제스처·다이얼로그를 다 갖는다(디버그 용도라 슬라이스로 안 쪼갬).
 * 트리거는 `useDebugTap()` 이 돌려주는 onClick 콜백뿐 — 아무 엘리먼트에나 걸면 그 자리가 진입점이 된다
 * (현재는 `AppHeader` 의 타이틀 h1). 다이얼로그는 `present()` 로 루트 슬롯에 명령형 mount 하므로
 * 호출처는 open state·JSX 를 들 필요가 없다.
 *
 * 빌드 게이팅 없음(운영 포함) — 진입 경로가 히든 제스처뿐이고, 값도 사용자 본인 디바이스 정보라
 * CS 대응 시 "복사"로 받아내는 편익이 크다. 노출 값 추가는 `rows` 한 줄.
 */

/** 연속 탭 인정 횟수 / 최대 간격(ms) — 간격을 넘기면 카운트 리셋. */
const TAP_COUNT = 5;
const TAP_WINDOW_MS = 800;

/**
 * 히든 제스처 콜백 — N연타 시 디버그 다이얼로그를 연다. 설정 화면(`ROUTES.SETTINGS`)에서만 활성이고
 * 그 외엔 `undefined` 를 돌려주니, 호출처는 `onClick={useDebugTap()}` 한 줄이면 끝(비활성=핸들러 미부착).
 */
export function useDebugTap(): (() => void) | undefined {
  const enabled = useLocation().pathname === ROUTES.SETTINGS;
  const taps = useRef({ count: 0, at: 0 });
  const onTap = useCallback(() => {
    const now = Date.now();
    const { count, at } = taps.current;
    const next = now - at > TAP_WINDOW_MS ? 1 : count + 1;
    taps.current = { count: next === TAP_COUNT ? 0 : next, at: now };
    if (next === TAP_COUNT) openDebugDialog();
  }, []);
  return enabled ? onTap : undefined;
}

/** 다이얼로그를 루트 슬롯에 명령형 mount(같은 key 가 열려 있으면 재mount 안 함 — 연타 스택 방지). */
function openDebugDialog(): void {
  present(({ open, close }) => <DebugDialog open={open} onClose={close} />, { key: 'debug' });
}

function DebugDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const fcmToken = useDebugStore((s) => s.fcmToken);
  const [copied, setCopied] = useState(false);

  // 같은 semver 로 여러 번 빌드되므로(EAS remote 카운터) 빌드 넘버까지 붙여야 실제 식별자가 된다.
  const nativeVer = getNativeAppVersion();
  const nativeBuild = getNativeBuildNumber();

  const rows: Array<[string, string]> = [
    ['ENV', DEPLOY_MODE],
    ['PLATFORM', getAppType()],
    // 웹 번들과 native 셸은 OTA 로 따로 굴러가 버전이 갈리므로 두 축을 분리 표기.
    // (브라우저 실행이면 native 축 자체가 없음 — 빈 값 대신 `(not native)` 로 부재를 명시)
    ['WEB_VER', getWebVersion()],
    [
      'NATIVE_VER',
      nativeVer ? `${nativeVer}${nativeBuild ? ` (${nativeBuild})` : ''}` : '(not native)',
    ],
    // preview 빌드는 env·번들 ID·웹 mode 가 production 과 동일해 채널만이 유일한 구분점.
    ['CHANNEL', getNativeChannel() ?? '(not native)'],
    ['TERMINAL', getTerminalId()],
    ['FCM', fcmToken ?? '(not issued)'],
  ];
  const text = rows.map(([k, v]) => `${k}: ${v}`).join('\n');

  const copy = () => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <FullScreenDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={UI_TEXT.SETTINGS_DEBUG_TITLE}
    >
      <div className="flex flex-col gap-3">
        {/* 우상단 고정 close 버튼과 겹치지 않게 제목 줄은 우측 여백 확보. */}
        <h2 className="pr-12 text-lg font-semibold text-foreground">
          {UI_TEXT.SETTINGS_DEBUG_TITLE}
        </h2>
        {/* 콘텐츠 배경보다 밝은 elevated 면 — 회색 muted 면은 다이얼로그 배경과 겹쳐 면이 안 선다. */}
        <pre
          className={cn(
            ELEVATED_SURFACE,
            'select-text overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-card p-3 font-mono text-xs text-muted-foreground',
          )}
        >
          {text}
        </pre>
        <Button variant="outline" size="sm" className="self-start" onClick={copy}>
          {copied ? UI_TEXT.SETTINGS_DEBUG_COPIED : UI_TEXT.SETTINGS_DEBUG_COPY}
        </Button>
      </div>
    </FullScreenDialog>
  );
}
